import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HomeOutlined } from '@ant-design/icons';
import { notification } from 'antd';
import {
  DEPTS, CapabilityKey, DeptCategory, HALL_FLOORS, HALL_PAGES,
} from './office-config';
import { useOfficeEngine, OfficeAgent, WorkspaceProject } from './useOfficeEngine';
import { useTheater } from './useTheater';
import { useVoiceHerald } from './useVoiceHerald';
import { CommandConsole, TaskDock, ReportDrawer, ProjectModal, deptOf } from './shared';
import ChatLayer from './ChatLayer';
import DevTeamOffice from './DevTeamOffice';
import ConfirmCenter from './ConfirmCenter';
import SessionArchive from './SessionArchive';
import ArtifactVault from './ArtifactVault';
import CursorFX from './CursorFX';
import CommandHallMode from './modes/CommandHallMode';
import { useOfficeAttachments } from './useOfficeAttachments';
import './AICommandOffice.css';
import './AICommandOfficeV8.css';
import './AICommandOfficeV81.css';
import './AICommandOfficeV9.css';

/* ============================================================
   AI 指挥办公室 v20 — 呈现方式定案为"沙盘展厅"单一形态
   （其余 14 种历史模式代码保留在 modes/ 目录，不再加载）。
   叠加拟真任务群聊过程层。后台链路零改动。
   ============================================================ */

const AICommandOffice: React.FC = () => {
  const engine = useOfficeEngine();
  const attachmentState = useOfficeAttachments();
  const { deptAgents, tasks } = engine;
  const theater = useTheater(tasks);

  /* 呈现方式定案：沙盘展厅 */
  const mode = 'hall' as const;
  const [selDeptId, setSelDeptId] = useState<string | null>(null);
  const [selAgentId, setSelAgentId] = useState<string | null>(null);
  const [selProject, setSelProject] = useState<WorkspaceProject | null>(null);
  const [cmd, setCmd] = useState('');
  const [capKey, setCapKey] = useState<CapabilityKey>('text');
  const [docFmt, setDocFmt] = useState<'word' | 'pdf'>('word');
  const [mentionOpen, setMentionOpen] = useState(false);
  const [drawerTaskId, setDrawerTaskId] = useState<string | null>(null);
  const [projectModal, setProjectModal] = useState(false);
  const [dispatching, setDispatching] = useState(false);
  const [booted, setBooted] = useState(false);
  const [pingDeptId, setPingDeptId] = useState<string | null>(null);
  const [scene, setScene] = useState<DeptCategory>('internal');
  const [devOffice, setDevOffice] = useState(false);
  /** 运镜聚焦：下达任务后镜头推进到该部门工作台 */
  const [focusDeptId, setFocusDeptId] = useState<string | null>(null);
  /** 会话档案（历次交办的工作台账） */
  const [archiveOpen, setArchiveOpen] = useState(false);
  /** 产物记录（历史生成的图片/PPT/文档） */
  const [vaultOpen, setVaultOpen] = useState(false);
  /** 语音播报开关（默认开，记忆） */
  const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem('aco.voice') !== 'off');
  useEffect(() => { localStorage.setItem('aco.voice', voiceOn ? 'on' : 'off'); }, [voiceOn]);
  const herald = useVoiceHerald(tasks, voiceOn);
  /** 新建会话按钮状态（busy / 成功反馈） */
  const [sessBusy, setSessBusy] = useState(false);
  const [sessOk, setSessOk] = useState(false);

  const consoleRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => { const t = setTimeout(() => setBooted(true), 60); return () => clearTimeout(t); }, []);

  /* ---------- 预加载：背景视频+全部工作台/特效素材就绪才放行进场 ----------
     背景是视频，弱网直接进场会黑屏或静止，体验极差。 */
  const [bootPct, setBootPct] = useState(0);
  const [bootDone, setBootDone] = useState(false);
  useEffect(() => {
    let alive = true;
    const imgs = Array.from(new Set([
      ...HALL_PAGES.internal.map((e) => e.img),
      ...HALL_PAGES.external.map((e) => e.img),
      HALL_FLOORS[0].img,
      '/aco-assets/fx-beam-a32-strip.webp',
      '/aco-assets/fx-ring-a32-strip.webp',
      '/aco-assets/fx-jet-a32-strip.webp',
      '/aco-assets/power-icon.webp',
    ]));
    const video = HALL_FLOORS.find((f) => f.video)?.video;
    const total = imgs.length + (video ? 1 : 0);
    let done = 0;
    const bump = () => { done += 1; if (alive) setBootPct(Math.min(99, Math.round((done / total) * 100))); };
    const ps: Array<Promise<void>> = imgs.map((src) => new Promise<void>((res) => {
      const im = new Image();
      im.onload = () => { bump(); res(); };
      im.onerror = () => { bump(); res(); };
      im.src = src;
    }));
    if (video) {
      ps.push(new Promise<void>((res) => {
        let fired = false;
        const ok = () => { if (fired) return; fired = true; bump(); res(); };
        const v = document.createElement('video');
        v.preload = 'auto';
        v.muted = true;
        v.src = video;
        v.oncanplaythrough = ok;
        v.onerror = ok;
        setTimeout(ok, 30000); /* 极端弱网兜底，30s 后放行 */
        v.load();
      }));
    }
    const t0 = Date.now();
    Promise.all(ps).then(() => {
      if (!alive) return;
      setBootPct(100);
      /* 至少露脸 0.9s（防闪）+ 0.65s 淡出 */
      const hold = Math.max(0, 900 - (Date.now() - t0));
      setTimeout(() => { if (alive) setBootDone(true); }, hold + 680);
    });
    return () => { alive = false; };
  }, []);

  /* 随机"部门心跳"：任何呈现方式下页面都活着 */
  useEffect(() => {
    const timer = setInterval(() => {
      const d = DEPTS[Math.floor(Math.random() * DEPTS.length)];
      setPingDeptId(d.id);
      setTimeout(() => setPingDeptId((v) => (v === d.id ? null : v)), 1500);
    }, 4200);
    return () => clearInterval(timer);
  }, []);

  const selDept = selDeptId ? deptOf(selDeptId) : null;
  const agents = selDeptId ? (deptAgents[selDeptId] || []) : [];
  const selAgent = agents.find((a) => a.id === selAgentId) || null;

  const runningByDept = useMemo(() => {
    const m: Record<string, typeof tasks[number] | undefined> = {};
    for (const t of tasks) {
      if ((t.status === 'running' || t.status === 'dispatching') && !m[t.deptId]) m[t.deptId] = t;
    }
    return m;
  }, [tasks]);

  const drawerTask = tasks.find((t) => t.localId === drawerTaskId) || null;

  const chooseDept = useCallback((deptId: string, agentId?: string) => {
    setScene(deptOf(deptId).category);
    setSelDeptId(deptId);
    /* 领导可以连续给多个部门派活：切部门即退出运镜聚焦，回到全景继续点将 */
    setFocusDeptId((v) => (v && v !== deptId ? null : v));
    const list = deptAgents[deptId] || [];
    const target = (agentId && list.find((a) => a.id === agentId && a.available)) || list.find((a) => a.kind === 'general');
    setSelAgentId(target?.id || null);
    setSelProject(null);
    setMentionOpen(false);
    setCmd((c) => c.replace(/@\s*$/, ''));
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [deptAgents]);

  const chooseAgent = useCallback((a: OfficeAgent) => {
    if (!a.available) return;
    if (a.deptId !== selDeptId) setSelDeptId(a.deptId);
    setSelAgentId(a.id);
    if (a.kind === 'devteam') setProjectModal(true);
    else setSelProject(null);
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [selDeptId]);

  const canSend = !!(
    selDept
    && selAgent
    && (cmd.trim() || attachmentState.usableAttachments.length)
    && !attachmentState.uploading
    && !dispatching
    && (selAgent.kind !== 'devteam' || selProject)
  );

  const handleSend = useCallback(async () => {
    if (!selDept || !selAgent) return;
    if (selAgent.kind === 'devteam' && !selProject) { setProjectModal(true); return; }
    if ((!cmd.trim() && !attachmentState.usableAttachments.length) || attachmentState.uploading || dispatching) return;
    const command = cmd.trim() || '请分析引用的附件，并给出清晰、完整的处理结果';
    const attachmentPayload = attachmentState.buildPayload();
    setDispatching(true);
    try {
      let dispatchedId: string | null = null;
      if (selAgent.kind === 'general') {
        if (capKey === 'ppt') dispatchedId = await engine.dispatchPpt(selDept, command, attachmentPayload);
        else if (capKey === 'design') dispatchedId = await engine.dispatchDesign(selDept, command, attachmentPayload);
        else dispatchedId = await engine.dispatchWorkshop(selDept, selAgent.name, command, capKey === 'doc' ? docFmt : capKey, attachmentPayload);
      }
      else if (selAgent.kind === 'cabin') dispatchedId = await engine.dispatchCabin(selDept, selAgent, command, attachmentPayload);
      else if (selAgent.kind === 'devteam' && selProject) dispatchedId = await engine.dispatchDevTeam(selDept, selProject, command, attachmentPayload);
      // 派发接口拒绝时保留指令和附件，便于用户直接重试，不让已完成的 OCR 白做。
      if (!dispatchedId) return;
      setCmd('');
      attachmentState.clearAttachments();
      /* 沙盘展厅：令牌飞抵后运镜推进到该部门工作台 */
      if (mode === 'hall') {
        const did = selDept.id;
        setTimeout(() => setFocusDeptId(did), 1050);
      }
    } finally {
      setDispatching(false);
    }
  }, [selDept, selAgent, selProject, cmd, dispatching, capKey, docFmt, engine, mode, attachmentState]);

  const closeDept = useCallback(() => setSelDeptId(null), []);
  const openTask = useCallback((id: string) => { setDrawerTaskId(id); herald.markViewed(id); }, [herald]);

  const modeProps = {
    deptAgents,
    theater,
    selDeptId,
    selAgentId,
    pingDeptId,
    onChooseDept: chooseDept,
    onCloseDept: closeDept,
    onPickAgent: chooseAgent,
    onOpenTask: openTask,
  };

  return (
    <div
      className={`aco3 aco8 mode-${mode} ${booted ? 'booted' : ''}`}
      onClick={() => { setMentionOpen(false); setSelDeptId(null); setFocusDeptId(null); }}
    >
      {/* 呈现主体：沙盘展厅（唯一形态） */}
      <CommandHallMode
        {...modeProps}
        scene={scene}
        setScene={setScene}
        tasks={tasks}
        runningByDept={runningByDept}
        focusDeptId={focusDeptId}
        onExitFocus={() => setFocusDeptId(null)}
      />

      {/* 顶部品牌条 */}
      <header className="aco3-head">
        <button className="aco3-home" title="返回工作台" onClick={() => { window.location.href = '/'; }}>
          <HomeOutlined />
        </button>
        <div className="aco3-title">
          <h1>AI 指挥办公室</h1>
          <span>高速公路智慧管理 · @部门交办，产物直达</span>
        </div>
        <button className="aco19-btn" onClick={(e) => { e.stopPropagation(); setArchiveOpen(true); }}>
          🗂 会话档案
        </button>
        <button className="aco23-btn" onClick={(e) => { e.stopPropagation(); setVaultOpen(true); }}>
          🗃 产物记录
        </button>
        <div className="aco3-legend">
          <button className={`lg in ${scene === 'internal' ? 'cur' : ''}`} onClick={(e) => { e.stopPropagation(); setScene('internal'); }}><i>●</i> 业主单位</button>
          <button className={`lg ex ${scene === 'external' ? 'cur' : ''}`} onClick={(e) => { e.stopPropagation(); setScene('external'); }}><i>⬡</i> 外部协作</button>
        </div>
      </header>

      {/* 任务坞 */}
      <TaskDock tasks={tasks} onOpen={openTask} />

      {/* 拟真任务群聊过程层（方案五，叠加所有模式） */}
      <ChatLayer tasks={tasks} theater={theater} onOpenTask={openTask} />

      {/* 指挥台 */}
      {/* 会话卡：左下角、任务工作群图标正下方（顶层元素——严禁放进带 transform 的容器，fixed 会失效） */}
      <div className="aco22-session" onClick={(e) => e.stopPropagation()}>
        <span className="cur" title="当前会话，追问都记在这里">💬 {engine.sessionTitle || '沿用最近会话'}</span>
        <button
          disabled={sessBusy}
          onClick={async () => {
            setSessBusy(true);
            try {
              await engine.startNewSession();
              setSessOk(true);
              setTimeout(() => setSessOk(false), 2600);
            } catch { window.alert('新建会话失败，请稍后再试'); }
            setSessBusy(false);
          }}
        >
          {sessBusy ? '正在开…' : sessOk ? '✓ 已切换到新会话' : '＋ 新建会话'}
        </button>
        <button className={`vc ${voiceOn ? 'on' : ''}`} onClick={() => setVoiceOn((v) => !v)}>
          {voiceOn ? '🔊 播报开' : '🔇 播报关'}
        </button>
      </div>

      <CommandConsole
        selDept={selDept}
        selAgent={selAgent}
        agents={agents}
        selProject={selProject}
        cmd={cmd}
        setCmd={setCmd}
        capKey={capKey}
        setCapKey={setCapKey}
        docFmt={docFmt}
        setDocFmt={setDocFmt}
        mentionOpen={mentionOpen}
        setMentionOpen={setMentionOpen}
        dispatching={dispatching}
        canSend={canSend}
        attachments={attachmentState.attachments}
        attachmentUploading={attachmentState.uploading}
        onAddAttachments={attachmentState.addFiles}
        onRemoveAttachment={attachmentState.removeAttachment}
        onRetryAttachment={attachmentState.retryAttachment}
        onSend={handleSend}
        onChooseDept={chooseDept}
        onChooseAgent={chooseAgent}
        inputRef={inputRef}
        consoleRef={consoleRef}
      />

      {/* 自定义 HUD 光标（隐藏系统光标，随状态变形） */}
      <CursorFX />

      {/* 确认反馈中心：数字人团队的问题在指挥办公室提醒并办理 */}
      <ConfirmCenter />

      {/* 会话档案 */}
      {archiveOpen && (
        <SessionArchive
          currentSessionId={engine.sessionId}
          onContinueSession={(session) => {
            engine.continueSession(session);
            notification.success({
              message: '已接续历史会话',
              description: `当前会话已切换为“${session.title || '历史会话'}”，后续通用智能体交办会自动结合最近 5 轮上下文。`,
              placement: 'topRight',
              duration: 6,
            });
            setTimeout(() => inputRef.current?.focus(), 120);
          }}
          onClose={() => setArchiveOpen(false)}
        />
      )}

      {/* 产物记录 */}
      {vaultOpen && <ArtifactVault onClose={() => setVaultOpen(false)} />}

      {/* 汇报抽屉 */}
      {drawerTask && (
        <ReportDrawer
          task={drawerTask}
          onClose={() => setDrawerTaskId(null)}
          onDownload={() => engine.downloadArtifact(drawerTask).catch((e) => window.alert(e?.message || '下载失败'))}
          onEnterDevOffice={selProject ? () => setDevOffice(true) : undefined}
        />
      )}

      {/* 研发办公室实况（只读展示）；返回时回"研发进行时"而非展厅 */}
      {devOffice && selProject && (
        <DevTeamOffice project={selProject} onClose={() => { setDevOffice(false); setProjectModal(true); }} />
      )}
      {!devOffice && selProject && selAgent?.kind === 'devteam' && (
        <button className="aco14-office-btn" onClick={(e) => { e.stopPropagation(); setDevOffice(true); }}>
          🤖 研发办公室实况 · {selProject.name}
        </button>
      )}

      {/* 进场装载页：全部关键素材就绪才放行 */}
      {!bootDone && (
        <div className={`aco31-boot ${bootPct >= 100 ? 'out' : ''}`}>
          <div className="grid" />
          <div className="core">
            <span className="emblem"><i className="ring" /><i className="ring r2" /><i className="dotc" /></span>
            <b>AI 指挥办公室</b>
            <em>高速公路智慧管理</em>
          </div>
          <div className="bar"><i style={{ width: `${bootPct}%` }} /></div>
          <span className="tip">{bootPct >= 100 ? '就绪，进入指挥现场' : `正在装载指挥现场… ${bootPct}%`}</span>
        </div>
      )}

      {/* 数字人项目选择 */}
      {projectModal && (
        <ProjectModal
          engine={engine}
          onCancel={() => setProjectModal(false)}
          onPick={(p) => { setSelProject(p); setProjectModal(false); setDevOffice(true); setTimeout(() => inputRef.current?.focus(), 50); }}
        />
      )}
    </div>
  );
};

export default AICommandOffice;
